import React from "react";
import Cta from "./cta";
import styles from "./styles.module.scss";
import clsx from "clsx";
import ContactForm from "./contact-form";
import Thanks from "./thanks";
import DownloadForm from "./download-form";
import { useChatbotConfig } from "../../../contexts/chatbot-config";
import { useBottomSheet } from "../../../contexts/bottom-sheet";
import type { FormProps, DownloadProps } from "./types";
export type { Content, FormProps, DownloadProps } from "./types";

// デフォルトのフォールバック値（APIデータがない場合のみ使用）
const defaultFormProps: FormProps = {
  title: "フォームデータを読み込んでいます...",
  page: [],
};

const defaultDownloadProps: DownloadProps = {
  url: "",
  form: false,
  title: "ダウンロードデータを読み込んでいます...",
  page: [],
};

export default function BottomSheet() {
  const { bottomViewData, chatbotData } = useChatbotConfig();
  const { open, content, selectedCtaIndex, setOpen, setContent } = useBottomSheet();

  // ドラッグ関連のstate
  const [isDragging, setIsDragging] = React.useState(false);
  const [startY, setStartY] = React.useState(0);
  const [currentY, setCurrentY] = React.useState(0);
  const [contentHeight, setContentHeight] = React.useState(0);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // bottomViewDataがない、またはdata配列が空の場合は何も表示しない
  const bottomViewObj = React.useMemo(() => {
    if (!bottomViewData) return null;
    return Array.isArray(bottomViewData) ? bottomViewData[0] : bottomViewData;
  }, [bottomViewData]);

  // 実際のデータ配列の長さをチェック
  const hasBottomViewData = bottomViewObj && bottomViewObj.data && bottomViewObj.data.length > 0;

  // basicプランの権限が権限1の場合は表示しない
  const isRestricted = React.useMemo(() => {
    if (!chatbotData?.subscription_info) return false;
    const basicPlan = chatbotData.subscription_info.basic_plan;
    return basicPlan?.plan_type === 'basic' && basicPlan?.plan_number === 1;
  }, [chatbotData]);

  // bottom_view_dataからformPropsとdownloadPropsを生成（早期リターン前に実行）
  const dynamicFormProps: FormProps = React.useMemo(() => {
    if (!bottomViewData) {
      console.log('[dynamicFormProps] bottomViewDataがありません');
      return defaultFormProps;
    }
    const dataArray = Array.isArray(bottomViewData) ? bottomViewData : [bottomViewData];

    // 選択されたCTAアイテムのフォームデータを取得
    const firstItem = dataArray[0];

    // data_sortでソート（CTAと同じ順序にする）
    const sortedData = (firstItem?.data || []).slice().sort((a: any, b: any) => {
      const sortA = typeof a.data_sort === 'string' ? parseInt(a.data_sort) : a.data_sort;
      const sortB = typeof b.data_sort === 'string' ? parseInt(b.data_sort) : b.data_sort;
      return (sortA || 0) - (sortB || 0);
    });

    const selectedData = sortedData[selectedCtaIndex];

    console.log('[dynamicFormProps] selectedCtaIndex:', selectedCtaIndex);
    console.log('[dynamicFormProps] selectedData:', selectedData);
    console.log('[dynamicFormProps] selectedData.page_details:', selectedData?.page_details);

    if (!selectedData) {
      console.log('[dynamicFormProps] selectedDataがありません');
      return defaultFormProps;
    }

    // type=1のフォームデータのみ処理
    if (selectedData.type !== 1 && selectedData.type !== "1") {
      console.log('[dynamicFormProps] type=1ではありません:', selectedData.type);
      return defaultFormProps;
    }

    // APIデータをFormProps形式に変換
    if (!selectedData.page_details || selectedData.page_details.length === 0) {
      console.log('[dynamicFormProps] page_detailsがありません');
      return defaultFormProps;
    }

    // 多步表单转換逻辑：
    // - 前两个name字段（漢字、カナ）合并到第一步
    // - 每个name字段包含两个输入框（姓、名）
    // - 其他字段2つずつ1ページに表示
    const pages: Array<{ list: any[] }> = [];
    const nameFields: any[] = [];
    const regularFields: any[] = [];  // 通常フィールドを一時保存

    selectedData.page_details.forEach((inputField) => {

      if (inputField.type === "name") {
        // name类型字段：生成两个输入框（姓、名）
        const formItem = {
          form_index: String(nameFields.length + 1),
          form_name: inputField.title,
          required: !!inputField.required,
          fields: [
            {
              type: "text" as React.HTMLInputTypeAttribute,
              name: `${inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, '')}-sei`,
              label: "姓",
              placeholder: inputField.placeholder || "",
              required: !!inputField.required,
            },
            {
              type: "text" as React.HTMLInputTypeAttribute,
              name: `${inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, '')}-mei`,
              label: "名",
              placeholder: inputField.placeholder || "",
              required: !!inputField.required,
            }
          ]
        };
        nameFields.push(formItem);
      } else {
        // nameフィールドが溜まっている場合、先にページに追加
        if (nameFields.length > 0) {
          pages.push({ list: [...nameFields] });
          nameFields.length = 0;
        }

        let pageFields: any[] = [];

        if (inputField.type === "date") {
          const fieldData = inputField as any;
          pageFields = [{
            type: "picker" as React.HTMLInputTypeAttribute,
            name: inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, ''),
            placeholder: inputField.placeholder || inputField.form_text || "",
            required: !!inputField.required,
            start: fieldData.start || new Date().getFullYear(), // APIから取得、デフォルトは今年
            length: fieldData.length || 5, // APIから取得、デフォルトは5年
            calc: fieldData.calc || "ADD", // APIから取得、デフォルトはADD
            includeTime: false  // 日付のみ (YYYY MM DD)
          }];
        } else if (inputField.type === "datetime-local") {
          // datetime-local: YYYY MM DD hh ii 形式のピッカー（30分単位）
          const fieldData = inputField as any;
          pageFields = [{
            type: "picker" as React.HTMLInputTypeAttribute,
            name: inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, ''),
            placeholder: inputField.placeholder || inputField.form_text || "",
            required: !!inputField.required,
            start: fieldData.start || new Date().getFullYear(), // APIから取得、デフォルトは今年
            length: fieldData.length || 5, // APIから取得、デフォルトは5年
            calc: fieldData.calc || "ADD", // APIから取得、デフォルトはADD
            includeTime: true  // 日付と時間 (YYYY MM DD hh ii, 30分間隔)
          }];
        } else if (inputField.type === "select") {
          // select: form_textをカンマで区切ってオプションリストに変換
          const formText = inputField.form_text || "";
          const options = formText
            .split(',')
            .map((opt: string) => opt.trim())
            .filter((opt: string) => opt !== "")
            .map((opt: string) => ({ value: opt }));

          pageFields = [{
            type: "select" as any,
            name: inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, ''),
            placeholder: inputField.placeholder || "",
            required: !!inputField.required,
            options: options
          }];
        } else {
          // 通常のフィールド
          pageFields = [{
            type: inputField.type as React.HTMLInputTypeAttribute,
            name: inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, ''),
            placeholder: inputField.placeholder || inputField.form_text || "",
            required: !!inputField.required,
          }];
        }

        // 通常フィールドを配列に追加
        regularFields.push({
          form_index: String(regularFields.length + 1),
          form_name: inputField.title,
          required: !!inputField.required,
          fields: pageFields
        });

        // 2つ溜まったらページに追加
        if (regularFields.length === 2) {
          pages.push({ list: [...regularFields] });
          regularFields.length = 0;
        }
      }
    });

    // 处理剩余的name字段
    if (nameFields.length > 0) {
      pages.push({ list: [...nameFields] });
    }

    // 残りの通常フィールドをページに追加
    if (regularFields.length > 0) {
      pages.push({ list: [...regularFields] });
    }

    const convertedFormProps: FormProps = {
      title: selectedData.form_title || selectedData.form_text || "",
      page: pages
    };

    console.log('[dynamicFormProps] 変換完了:', {
      title: convertedFormProps.title,
      pageCount: convertedFormProps.page.length,
      pages: convertedFormProps.page
    });

    return convertedFormProps;
  }, [bottomViewData, selectedCtaIndex]);

  const dynamicDownloadProps = React.useMemo(() => {
    if (!bottomViewData) {
      return defaultDownloadProps;
    }

    // 配列か単一オブジェクトかを判定
    const bottomViewObj = Array.isArray(bottomViewData) ? bottomViewData[0] : bottomViewData;

    // data_sortで並び替え（CTAと同じ順序にする）
    const sortedData = (bottomViewObj?.data || []).slice().sort((a: any, b: any) => {
      const sortA = typeof a.data_sort === 'string' ? parseInt(a.data_sort) : a.data_sort;
      const sortB = typeof b.data_sort === 'string' ? parseInt(b.data_sort) : b.data_sort;
      return (sortA || 0) - (sortB || 0);
    });

    // selectedCtaIndexのアイテムを取得（dl_typeの判定用）
    const selectedData = sortedData[selectedCtaIndex];

    if (!selectedData) {
      return defaultDownloadProps;
    }

    const isDlType = selectedData.dl_type === 1 || selectedData.dl_type === "1";

    if (!isDlType) {
      return defaultDownloadProps;
    }

    // dl_type=1: ダウンロードフォーム
    // ダウンロードリンクがある場合
    if (selectedData.link && (!selectedData.page_details || selectedData.page_details.length === 0)) {
      // フォームなしの直接ダウンロード
      return {
        url: selectedData.link,
        download_files: selectedData.download_files || [],
        form: false,
        title: selectedData.form_text || "",
        page: []
      };
    }

    // フォームがある場合
    if (selectedData.page_details && selectedData.page_details.length > 0) {

      // 多步表单转換逻辑（与上面相同）
      const downloadPages: Array<{ list: any[] }> = [];
      const downloadNameFields: any[] = [];

      selectedData.page_details.forEach((inputField: any) => {
        if (inputField.type === "name") {
          // name类型字段：生成两个输入框（姓、名）
          const formItem = {
            form_index: String(downloadNameFields.length + 1),
            form_name: inputField.title,
            required: !!inputField.required,
            fields: [
              {
                type: "text" as React.HTMLInputTypeAttribute,
                name: `${inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, '')}-sei`,
                label: "姓",
                placeholder: inputField.placeholder || "",
                required: !!inputField.required,
              },
              {
                type: "text" as React.HTMLInputTypeAttribute,
                name: `${inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, '')}-mei`,
                label: "名",
                placeholder: inputField.placeholder || "",
                required: !!inputField.required,
              }
            ]
          };
          downloadNameFields.push(formItem);
        } else {
          if (downloadNameFields.length > 0) {
            downloadPages.push({ list: [...downloadNameFields] });
            downloadNameFields.length = 0;
          }

          let pageFields: any[] = [];

          if (inputField.type === "date") {
            const fieldData = inputField as any;
            pageFields = [{
              type: "picker" as React.HTMLInputTypeAttribute,
              name: inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, ''),
              placeholder: inputField.placeholder || inputField.form_text || "",
              required: !!inputField.required,
              start: fieldData.start || new Date().getFullYear(),
              length: fieldData.length || 5,
              calc: fieldData.calc || "ADD",
              includeTime: false  // 日付のみ (YYYY MM DD)
            }];
          } else if (inputField.type === "datetime-local") {
            // datetime-local: YYYY MM DD hh ii 形式のピッカー（30分単位）
            const fieldData = inputField as any;
            pageFields = [{
              type: "picker" as React.HTMLInputTypeAttribute,
              name: inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, ''),
              placeholder: inputField.placeholder || inputField.form_text || "",
              required: !!inputField.required,
              start: fieldData.start || new Date().getFullYear(),
              length: fieldData.length || 5,
              calc: fieldData.calc || "ADD",
              includeTime: true  // 日付と時間 (YYYY MM DD hh ii, 30分間隔)
            }];
          } else {
            // 通常のフィールド
            pageFields = [{
              type: inputField.type as React.HTMLInputTypeAttribute,
              name: inputField.title.toLowerCase().replace(/[（）\s]+/g, '-').replace(/^-+|-+$/g, ''),
              placeholder: inputField.placeholder || inputField.form_text || "",
              required: !!inputField.required,
            }];
          }

          downloadPages.push({
            list: [{
              form_index: String(downloadPages.length + 1),
              form_name: inputField.title,
              required: !!inputField.required,
              fields: pageFields
            }]
          });
        }
      });

      // 处理剩余的name字段
      if (downloadNameFields.length > 0) {
        downloadPages.push({ list: [...downloadNameFields] });
      }

      const convertedDownloadProps: DownloadProps = {
        url: selectedData.link || "",
        download_files: selectedData.download_files || [],
        form: true,
        title: selectedData.form_title || selectedData.form_text || "",
        page: downloadPages
      };

      return convertedDownloadProps;
    }

    return defaultDownloadProps;
  }, [bottomViewData, selectedCtaIndex]);

  // 表示制限がある場合は空のプレースホルダーを返す（すべてのHooks呼び出し後）
  if (!hasBottomViewData || isRestricted) {
    return <div style={{ height: '22px', position: 'relative', zIndex: 4 }} />;
  }

  function handleClick() {
    // 制限チェック
    if (!hasBottomViewData || isRestricted) {
      return;
    }

    setOpen(!open);
    if (!open) {
      setContent("CTA");
    }
  }

  // --- Pointer Events (Mouse & Touch) ---
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!hasBottomViewData || isRestricted) return;
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    setIsDragging(true);
    setStartY(e.clientY);
    setCurrentY(e.clientY);

    if (contentRef.current) {
      const rect = contentRef.current.getBoundingClientRect();
      setContentHeight(rect.height);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    e.preventDefault(); // デフォルト動作を防止
    e.stopPropagation();
    setCurrentY(e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).releasePointerCapture(e.pointerId);
    setIsDragging(false);

    const deltaY = currentY - startY;
    const swipeThreshold = 15;

    // ドラッグ処理のみ実行（タップ処理は.headのonClickで処理）
    if (open) {
      if (deltaY > swipeThreshold) setOpen(false);
    } else {
      if (deltaY < -swipeThreshold) {
        setOpen(true);
        setContent("CTA");
      }
    }

    setStartY(0);
    setCurrentY(0);
  };

  return (
    <div>
      <div
        className={clsx(styles.overlay, open && styles.overlayOpen)}
        onClick={() => setOpen(false)}
        style={{ pointerEvents: open ? 'auto' : 'none' }}
      />
      <div className={styles.bottomSheet} style={{ pointerEvents: 'auto' }}>
        <div
          ref={contentRef}
          className={clsx(styles.content, open && styles.isOpen)}
          data-bottom-sheet-content
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            transform: (() => {
              if (!isDragging || currentY === startY) return undefined;
              const delta = currentY - startY;
              const maxOffset = Math.max(0, (contentHeight || 0) - 44);
              const clampedDelta = open
                ? Math.min(Math.max(delta, 0), maxOffset)
                : Math.max(Math.min(delta, 0), -maxOffset);
              return `translateY(calc(${open ? '-100% + 44px' : '0px'} + ${clampedDelta}px))`;
            })(),
            transition: isDragging ? 'none' : undefined,
            touchAction: 'none'
          }}
        >
          <div className={styles.head} onClick={handleClick}>
            <button
              className={clsx(styles.button, open && styles.buttonOpen)}
              aria-label={open ? "Close" : "Open"}
              style={{ pointerEvents: 'none' }}
            >
              {open && <span aria-hidden="true">Close</span>}
            </button>
          </div>
          <div className={styles.body} key={`body-${content}-${selectedCtaIndex}`}>
            {content === "CTA" && (
              <Cta
                downloadForm={dynamicDownloadProps.form}
                downloadUrl={dynamicDownloadProps.url}
              />
            )}
            {content === "FORM" && (
              <ContactForm formProps={dynamicFormProps} />
            )}
            {content === "DOWNLOAD" && (
              <DownloadForm formProps={dynamicDownloadProps} />
            )}
            {content === "THANKS" && <Thanks setOpen={setOpen} />}
          </div>
        </div>
      </div>
    </div>
  );
}